import { computed, ref, watch } from 'vue';
import { WaypointPinIcon, TrackPinIcon, DrawingPinIcon, useHtmlHelpers, imageConstants } from 'ihunter-pathfinder-components';
import { CollectionType, CollectionItem } from 'ihunter-common';
import { OverlayPathData } from './useOverlayPathData';

const collectionConstants = () => {
    class PinSize {
        constructor(name) {
            this.name = name;
            this.cssClass = `pin-icon-${name.toLowerCase()}`;
            this.cssPropertyName = `--pin-icon-${name.toLowerCase()}-size`;
        }

        get strValue() {
            if (!this._strValue) {
                const { getCSSPropertyValue } = useHtmlHelpers();
                this._strValue = getCSSPropertyValue(this.cssPropertyName);
            }
            return this._strValue;
        }

        get intValue() {
            if (!this._intValue) {
                this._intValue = parseInt(this.strValue?.replace('px', '') || 0);
            }
            return this._intValue;
        }

        static get Map() {
            return new PinSize('Map');
        }

        static get Detail() {
            return new PinSize('Detail');
        }

        static get Preview() {
            return new PinSize('Preview');
        }
    }

    // Include only overlays with path data (temporary until we have assets for all overlays)
    const PinOverlays = CollectionItem.Waypoint.PinOverlays.filter((x) => OverlayPathData[x]);
    Object.freeze(PinOverlays);

    const PinColorVariation = {
        Default: '',
        Alt: '-alt',
        Alt2: '-alt2',
    };
    Object.freeze(PinColorVariation);

    return { PinSize, PinOverlays, PinColorVariation };
};

const useCollectionPin = () => {
    const { ImageFormat } = imageConstants();
    const { getCSSPropertyValue } = useHtmlHelpers();

    const getPinIconForCollectionType = (type) => {
        switch (type) {
            case CollectionType.Waypoint:
                return WaypointPinIcon;
            case CollectionType.Track:
                return TrackPinIcon;
            case CollectionType.Drawing:
                return DrawingPinIcon;
            default:
                throw new Error(`Unsupported collection type ${type}`);
        }
    };

    const getPinIconProps = (pinOverlay, pinBackground, pinSize, imageFormat) => {
        return {
            overlayName: pinOverlay,
            color: pinBackground,
            size: pinSize,

            // In rasterized images CSS classes are not included, so the style must be manually set
            manuallySetStyle: imageFormat === ImageFormat.Raster,
        };
    };

    const getPinColorValue = (colorName, pinVariation) => {
        const cssPropertyName = `--pin-${colorName}${pinVariation}`;
        return getCSSPropertyValue(cssPropertyName);
    };

    const getInkColorValue = (colorName) => {
        const cssPropertyName = `--ink-draw-${colorName}`;
        return getCSSPropertyValue(cssPropertyName);
    };

    return { getPinIconForCollectionType, getPinIconProps, getPinColorValue, getInkColorValue };
};

const collectionPinProps = (collectionType) => {
    const { PinSize } = collectionConstants();

    return {
        collectionType: {
            type: String,
            default: collectionType,
        },
        color: {
            type: String,
            default: CollectionItem[collectionType].Default.Color,
        },
        size: {
            type: [Object, Number],
            required: PinSize.Map,
        },
        overlayName: {
            type: String,
            default: null,
        },

        /* For image caching: Set manuallySetStyle to true to allow the style (fill, stroke, etc)
         * to be set directly on the html element, based on the CSS properties that correspond to the pin.
         * Required when rasterizing an svg element to an image, as CSS classes will not be included.
         * */
        manuallySetStyle: {
            type: Boolean,
            default: false,
        },
    };
};

const useCollectionPinComponent = (props) => {
    const { PinColorVariation } = collectionConstants();
    const { getPinColorValue } = useCollectionPin();

    const FILL_VARIATION = PinColorVariation.Default;
    const OUTLINE_VARIATION = PinColorVariation.Alt; // TODO: Support dark mode by using Alt2 for the outline variation.

    const color = computed(() => {
        const colors = CollectionItem[props.collectionType].Colors;
        const color = colors.includes(props.color) ? props.color : CollectionItem[props.collectionType].Default.Color;
        return color;
    });

    const size = computed(() => {
        return props.size?.intValue || props.size;
    });

    const fillClass = computed(() => {
        return `pin-fill-${color.value}${FILL_VARIATION}`;
    });

    const outlineClass = computed(() => {
        return `pin-fill-${color.value}${OUTLINE_VARIATION}`;
    });

    const initializeStyleFillColor = (ref) => {
        if (props.manuallySetStyle && ref.value) {
            const fillColor = getPinColorValue(color.value, FILL_VARIATION);
            if (fillColor) {
                ref.value.style.fill = fillColor;
            } else {
                console.error(`initializeStyleFillColor failed for color '${color.value}'`);
            }
        }
    };

    const initializeStyleOutlineColor = (ref) => {
        if (props.manuallySetStyle && ref.value) {
            const outlineColor = getPinColorValue(color.value, OUTLINE_VARIATION);
            if (outlineColor) {
                ref.value.style.fill = outlineColor;
            } else {
                console.error(`initializeStyleOutlineColor failed for color '${color.value}'`);
            }
        }
    };

    return {
        color,
        size,
        fillClass,
        outlineClass,

        initializeStyleFillColor,
        initializeStyleOutlineColor,
    };
};

const usePinOverlayComponent = (props) => {
    const overlayPathData = ref(null);

    watch(
        () => props.overlayName,
        () => {
            initializeOverlayPathData();
        },
    );

    const overlayName = computed(() => {
        const overlays = CollectionItem.Waypoint.PinOverlays;
        return overlays.includes(props.overlayName) ? props.overlayName : null;
    });

    const initializeOverlayPathData = () => {
        if (overlayName.value) {
            overlayPathData.value = OverlayPathData[overlayName.value];
        }
    };

    return {
        overlayName,
        overlayPathData,
        initializeOverlayPathData,
    };
};

export { useCollectionPin, useCollectionPinComponent, usePinOverlayComponent, collectionConstants, collectionPinProps };
